import { useEffect } from "react";
import { useParams } from "react-router-dom";

import { OrderStatusBadge } from "../features/orders/OrderStatusBadge";
import { useMyOrder } from "../features/orders/useMyOrder";
import { formatDate } from "../lib/date";
import { messages } from "../lib/messages";
import { mainButton } from "../lib/telegram";

const FULFILLMENT_LABELS = {
  delivery: messages.fulfillmentDelivery,
  pickup: messages.fulfillmentPickup,
} as const;

/**
 * OrderDetailPage — карточка заказа покупателя (docs/07-miniapp-ux.md §2.7):
 * состав и данные. Отмены покупателем в MVP нет — только через менеджера,
 * поэтому у экрана нет действий, только чтение.
 */
export function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const order = useMyOrder(orderId ?? "");

  useEffect(() => {
    mainButton.hide();
  }, []);

  if (order.isPending) {
    return <OrderDetailSkeleton />;
  }

  if (order.isError) {
    return <p className="p-4 text-center text-hint">{messages.orderNotFound}</p>;
  }

  const found = order.data;

  return (
    <div className="min-h-screen bg-bg pb-6">
      <header className="flex items-center justify-between p-4">
        <h1 className="text-lg font-semibold text-text">
          {messages.orderNumberLabel}
          {found.number}
        </h1>
        <OrderStatusBadge status={found.status} label={found.status_text} />
      </header>
      <p className="px-4 text-sm text-hint">{formatDate(found.created_at)}</p>

      <ul className="flex flex-col gap-2 px-4 pt-3">
        {found.items.map((item, index) => (
          <li key={item.product_id ?? index} className="flex items-center gap-3 rounded-card bg-surface p-2">
            <div className="h-14 w-14 shrink-0 overflow-hidden rounded-card bg-bg">
              {item.image_url ? (
                <img src={item.image_url} alt={item.title} className="h-full w-full object-cover" />
              ) : null}
            </div>
            <div className="flex flex-1 flex-col gap-1">
              <p className="text-sm text-text">{item.title}</p>
              <span className="text-sm text-hint">
                {item.qty} × {item.price_text}
              </span>
            </div>
            <span className="text-text">{item.line_total_text}</span>
          </li>
        ))}
      </ul>

      <div className="mt-4 flex flex-col gap-1 px-4 text-sm">
        <div className="flex justify-between text-hint">
          <span>{messages.itemsTotal}</span>
          <span>{found.items_total_text}</span>
        </div>
        <div className="flex justify-between text-hint">
          <span>{messages.delivery}</span>
          <span>{found.delivery_price_text}</span>
        </div>
        <div className="flex justify-between text-base font-semibold text-text">
          <span>{messages.total}</span>
          <span>{found.total_text}</span>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-1 px-4 text-sm text-text">
        <p>{FULFILLMENT_LABELS[found.fulfillment]}</p>
        {found.address ? <p>{found.address}</p> : null}
        <p>
          {found.contact_name} · {found.contact_phone}
        </p>
        {found.comment ? <p className="text-hint">{found.comment}</p> : null}
      </div>
    </div>
  );
}

function OrderDetailSkeleton() {
  return (
    <div className="flex min-h-screen flex-col gap-3 bg-bg p-4">
      <div className="h-10 animate-pulse rounded-card bg-surface" />
      <div className="h-20 animate-pulse rounded-card bg-surface" />
    </div>
  );
}
